import { createFileRoute, notFound } from '@tanstack/react-router';
import { z } from 'zod';
import { CategoryShowPage } from '../features/categories';
import {
  categoryShowQuery,
  isNotFoundError,
  sidebarQuery,
  type CategoryShowData,
} from '../lib/queries';
import {
  articleUrl,
  categoryUrl,
  DEFAULT_OG,
  ogImageMeta,
  ORGANIZATION_ID,
  SITE_LOCALE,
  SITE_NAME,
  SITE_URL,
  WEBSITE_ID,
} from '../lib/seo';

// `page` is absent on page 1 rather than `?page=1`. Every navigation link used
// to carry `?page=1`, which is a second URL for content whose canonical is the
// bare category path: crawl budget spent on duplicates, and not one internal
// link pointing at the URL we actually want ranked. `catch` also folds any
// stray `?page=1` or `?page=abc` back onto the clean URL.
const SearchSchema = z.object({
  page: z.coerce.number().int().min(2).optional().catch(undefined),
});

function collectionJsonLd(opts: {
  name: string;
  url: string;
  articles: Array<{ slug: string; title: string }>;
}): string {
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': opts.url,
    url: opts.url,
    name: opts.name,
    inLanguage: 'ar',
    isPartOf: { '@id': WEBSITE_ID },
    publisher: { '@id': ORGANIZATION_ID },
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: opts.articles.length,
      itemListElement: opts.articles.map((a, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: articleUrl(a.slug),
        name: a.title,
      })),
    },
  });
}

/** Mirrors the visible breadcrumb trail so the SERP shows the section path
 *  instead of a bare URL. */
function breadcrumbJsonLd(
  chain: Array<{ name: string; slug: string }>,
  fallback: { name: string; url: string },
): string {
  const items: Array<Record<string, unknown>> = [
    { '@type': 'ListItem', position: 1, name: 'الرئيسية', item: SITE_URL },
  ];
  const trail = chain.length
    ? chain.map((c) => ({ name: c.name, item: categoryUrl(c.slug) }))
    : [{ name: fallback.name, item: fallback.url }];
  for (const crumb of trail) {
    items.push({
      '@type': 'ListItem',
      position: items.length + 1,
      name: crumb.name,
      item: crumb.item,
    });
  }
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  });
}

export const Route = createFileRoute('/category/$')({
  validateSearch: (search) => SearchSchema.parse(search),
  loaderDeps: ({ search }) => ({ page: search.page ?? 1 }),
  loader: async ({ context, params, deps }) => {
    const slug = params._splat ?? '';
    if (!slug) throw notFound();
    void context.queryClient.prefetchQuery(sidebarQuery());
    // Returned, not just awaited, so `head` can build the ItemList and the
    // pagination links from the real result set.
    let data: CategoryShowData;
    try {
      data = await context.queryClient.ensureQueryData(categoryShowQuery(slug, deps.page));
    } catch (err) {
      // Unknown category slug → 404. Previously the API error escaped and the
      // page answered 500, which Google treats as "come back later" instead of
      // "this section does not exist".
      if (isNotFoundError(err)) throw notFound();
      throw err;
    }
    // `?page=` is unbounded, and every out-of-range value used to answer 200
    // with an empty, self-canonical, indexable page — an infinite supply of
    // thin URLs, each one linking back to the previous.
    const totalPages = data.pagination?.totalPages ?? 1;
    if (deps.page > Math.max(totalPages, 1)) throw notFound();
    return data;
  },
  head: ({ params, match }) => {
    const slug = params._splat ?? '';
    const data = match.loaderData as CategoryShowData | undefined;
    const page = (match.search as { page?: number } | undefined)?.page ?? 1;


    const leaf = slug.split('/').filter(Boolean).pop() ?? slug;
    const readable = data?.category?.name ?? decodeURIComponent(leaf).replace(/-/g, ' ');
    const pageSuffix = page > 1 ? ` — صفحة ${page}` : '';
    const title = `${readable}${pageSuffix} - ${SITE_NAME}`;
    const desc = `أحدث الأخبار في تصنيف ${readable} على ${SITE_NAME}.`;
    // Self-referencing per page. Canonicalising page 2+ back to page 1 marks
    // those pages as duplicates, which drops every article reachable only
    // through deep pagination.
    const url = categoryUrl(slug, page);
    const totalPages = data?.pagination?.totalPages ?? 1;
    const articles = data?.articles ?? [];

    return {
      meta: [
        { title },
        { name: 'description', content: desc },
        { property: 'og:title', content: title },
        { property: 'og:description', content: desc },
        { property: 'og:url', content: url },
        { property: 'og:type', content: 'website' },
        { property: 'og:locale', content: SITE_LOCALE },
        ...ogImageMeta({ ...DEFAULT_OG, alt: readable }),
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: desc },
      ],
      links: [
        { rel: 'canonical', href: url },
        ...(page > 1 ? [{ rel: 'prev', href: categoryUrl(slug, page - 1) }] : []),
        ...(page < totalPages ? [{ rel: 'next', href: categoryUrl(slug, page + 1) }] : []),
      ],
      scripts: [
        ...(articles.length
          ? [
              {
                type: 'application/ld+json',
                children: collectionJsonLd({ name: readable, url, articles }),
              },
            ]
          : []),
        {
          type: 'application/ld+json',
          children: breadcrumbJsonLd(data?.category?.parentChain ?? [], {
            name: readable,
            url: categoryUrl(slug),
          }),
        },
      ],
    };
  },
  component: CategoryShowPage,
});
